import * as React from 'react';
import {
  keyframes,
  assignProps,
  createViewConfig,
  createGenericView,
} from '@sail/engine';
import {tokens} from '@sail/tokens';
import type {View} from '../../view';
import {view} from '../../view';
import {css} from '../../css';
import useDebouncedValue from '../utils/useDebouncedValue';

export type CodePuncherDigitData = {
  value: string;
  active?: boolean;
  disabled?: boolean;
  error?: boolean;
};

export type CodePuncherDigitProps = View.IntrinsicElement<
  'div',
  {
    hasCaret?: boolean;
    isHighlighted?: boolean;
  } & CodePuncherDigitData
>;

export const CodePuncherDigitConfig = createViewConfig({
  props: {} as CodePuncherDigitProps,
  name: 'CodePuncherDigit',
  defaults: {
    value: '',
  },
});

const caretBlinkKeyframes = keyframes({
  '0%, 70%, 100%': {
    opacity: 1,
  },
  '20%, 50%': {
    opacity: 0,
  },
});

const FakeCaret = createGenericView(() => {
  return (
    <view.div
      css={{
        stack: 'x',
        inset: 'space.0',
        position: 'absolute',
        pointerEvents: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        animation: `${caretBlinkKeyframes} 1.25s ease-out infinite`,
      }}
    >
      <view.div
        css={{
          width: 'xxsmall',
          height: 'xlarge',
          borderRadius: 'xsmall',
          backgroundColor: 'feedback.neutral.inverted',
        }}
      />
    </view.div>
  );
});

export const CodePuncherDigit = CodePuncherDigitConfig.createView(
  ({value, hasCaret, isHighlighted, ...props}) => {
    const isVisuallyHighlighted = useDebouncedValue(isHighlighted ?? false, 50);

    return (
      <view.div {...props}>
        <view.span
          css={[
            css({
              font: 'heading.xlarge.subdued',
              transition: `background-color ${tokens['animation.duration.short']} ${tokens['animation.easing']}`,
            }),
            isVisuallyHighlighted
              ? css({backgroundColor: tokens['color.primary.100']})
              : css({backgroundColor: 'transparent'}),
          ]}
        >
          {value}
        </view.span>
        {hasCaret && !value ? <FakeCaret /> : null}
      </view.div>
    );
  },
  {
    css: {
      width: 'space.500',
      height: 'xxlarge',

      position: 'relative',

      stack: 'x',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'form',

      color: 'form',

      cursor: 'pointer',
      userSelect: 'none',

      transitionProperty: 'all',
      transitionDuration: 'short',
      transitionTimingFunction: 'standard',

      ':first-child': {
        borderTopLeftRadius: 'medium',
        borderBottomLeftRadius: 'medium',
      },

      ':last-child': {
        borderTopRightRadius: 'medium',
        borderBottomRightRadius: 'medium',
      },
    },
    uses: [
      assignProps<CodePuncherDigitProps>((props) => ({
        hasCaret: props.active && !props.value,
        isHighlighted: props.active && !!props.value,
      })),
    ],
    variants: {
      disabled: {
        true: {
          css: {
            pointerEvents: 'none',
            color: 'form.disabled',
            backgroundColor: 'form.disabled',
          },
        },
        false: {},
      },
      active: {
        true: {
          css: {
            zIndex: 1,
            focusRing: 'focus',
          },
        },
        false: {
          css: {
            keyline: 'border',
          },
        },
      },
      error: {
        true: {
          css: {
            keyline: 'form.error.disabled',
          },
        },
        false: {},
      },
    },
  },
);
